import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Basic markdown config
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Harden links: open safely + block javascript: etc.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof HTMLElement)) return;

  if (node.tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    let safe = false;
    try {
      const url = new URL(src);
      safe = ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password;
    } catch { /* Invalid or incomplete image URL. */ }
    if (!safe) {
      node.replaceWith(document.createTextNode(node.getAttribute('alt') || 'Photo unavailable'));
      return;
    }
    node.removeAttribute('srcset');
    // Already loaded before: show it straight away instead of its placeholder.
    if (loadedPhotos.has(src)) node.classList.add('loaded');
    node.setAttribute('loading', 'lazy');
    node.setAttribute('decoding', 'async');
    node.setAttribute('referrerpolicy', 'no-referrer');
  }

  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    const lower = href.trim().toLowerCase();

    // Block dangerous protocols
    if (lower.startsWith('javascript:') || lower.startsWith('data:')) {
      node.removeAttribute('href');
    } else {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  }
});

/**
 * A reply streams in piece by piece, so a photo's markdown arrives as
 * "![Pool](https://ex…" before it is complete. Anything from an unfinished
 * image to the end is held back, so the URL is never shown as text.
 */
export function splitUnfinishedImage(md: string): { shown: string; unfinished: boolean } {
  const start = md.lastIndexOf('![');
  if (start === -1) return { shown: md, unfinished: false };
  const rest = md.slice(start);
  if (/^!\[[^\]\n]*\]\([^)\s]*\)/.test(rest)) return { shown: md, unfinished: false };
  // "![", "![alt", "![alt]", "![alt](" or "![alt](https://…" still arriving.
  if (/^!\[[^\]\n]*(\](\([^)\s]*)?)?$/.test(rest)) return { shown: md.slice(0, start), unfinished: true };
  return { shown: md, unfinished: false };
}

/** Shown where a photo is still arriving or loading. */
export const PHOTO_SKELETON = '<span class="photo-skeleton" role="img" aria-label="Loading photo"></span>';

/** Photos that have already loaded once, so a re-render doesn't show their placeholder again. */
export const loadedPhotos = new Set<string>();

function sanitize(md: string): string {
  const raw = marked.parse(md) as string;
  return DOMPurify.sanitize(raw, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'script', 'iframe'],
    FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload'],
  });
}

export function renderMarkdown(md: string): string {
  const { shown, unfinished } = splitUnfinishedImage(md);
  const safe = sanitize(shown);
  return unfinished ? safe + PHOTO_SKELETON : safe;
}

const PHOTO_LINE = /^!\[[^\]\n]*\]\([^)\s]+\)\s*$/;

/** A reply split into plain parts and runs of cards (each card: a photo line, then its details). */
export type ReplySegment = { kind: 'text'; md: string } | { kind: 'cards'; cards: string[] };

/**
 * Splits a reply into text and cards. A card starts with a photo alone on a
 * line and runs until the next blank line or the next photo line. Two or more
 * cards separated only by blank lines form a slider; a single card stays
 * part of the text.
 */
export function splitIntoSegments(md: string): ReplySegment[] {
  const lines = md.split('\n');
  const segments: ReplySegment[] = [];
  let text: string[] = [];
  let run: string[] = [];
  const flushText = () => {
    if (text.join('\n').trim()) segments.push({ kind: 'text', md: text.join('\n') });
    text = [];
  };
  const flushRun = () => {
    if (run.length >= 2) {
      flushText();
      segments.push({ kind: 'cards', cards: run });
    } else if (run.length === 1) {
      text.push(...run[0]!.split('\n'), '');
    }
    run = [];
  };

  let i = 0;
  while (i < lines.length) {
    if (PHOTO_LINE.test(lines[i]!)) {
      const card = [lines[i]!];
      i++;
      while (i < lines.length && lines[i]!.trim() !== '' && !PHOTO_LINE.test(lines[i]!)) card.push(lines[i++]!);
      run.push(card.join('\n'));
      while (i < lines.length && lines[i]!.trim() === '') i++;
      continue;
    }
    flushRun();
    text.push(lines[i]!);
    i++;
  }
  flushRun();
  flushText();
  return segments;
}

/**
 * Markdown split so each photo line is a block of its own: while the text next
 * to a photo is still streaming, only that text is redrawn and the photo keeps
 * its element (no reload, no flash).
 */
function blocks(md: string): string[] {
  const out: string[] = [];
  let text: string[] = [];
  for (const line of md.split('\n')) {
    if (PHOTO_LINE.test(line)) {
      if (text.join('\n').trim()) out.push(text.join('\n'));
      out.push(line);
      text = [];
    } else {
      text.push(line);
    }
  }
  if (text.join('\n').trim()) out.push(text.join('\n'));
  return out;
}

/** A reply as safe HTML: text blocks, and runs of cards (each card's blocks) shown side by side. */
export type ReplyPart = { kind: 'html'; html: string } | { kind: 'cards'; cards: string[][] };

export function renderReply(md: string): ReplyPart[] {
  const { shown, unfinished } = splitUnfinishedImage(md);
  const parts: ReplyPart[] = [];
  for (const segment of splitIntoSegments(shown)) {
    if (segment.kind === 'text') {
      for (const block of blocks(segment.md)) parts.push({ kind: 'html', html: sanitize(block) });
    } else {
      parts.push({ kind: 'cards', cards: segment.cards.map((card) => blocks(card).map(sanitize)) });
    }
  }
  if (unfinished) parts.push({ kind: 'html', html: PHOTO_SKELETON });
  return parts;
}
